import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, DatePicker, Flex, Row, Spin } from 'antd'
import { Dayjs } from 'dayjs'

import { useClassifierServiceGetIcd10AmClassifierElementListEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  Icd10AmElementType
} from 'src/api/requests'
import { DEFAULT_ONE_HUNDRED_PER_PAGE } from 'src/app-constants'
import { Text } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { readOnlySelectProps, useIsReadOnlyForm } from '../ReadOnly'

import { HivIcd10Select } from './HivIcd10Select'

type Entry = { icd10Id?: string; date?: Dayjs | null }

type Props = {
  value?: Entry[] | null
  onChange?: (value: Entry[]) => void
}

/**
 * 56. Opportunistic diseases, each with a date: the diseases listed on the form as checkboxes
 * (ICD-10-AM elements of type `opportunisticDisease`) and "Alte" as any other ICD-10-AM element.
 */
export const OpportunisticDiseasesField = ({ value, onChange }: Props) => {
  const { t, currentLang } = useLocales()
  const isReadOnly = useIsReadOnlyForm()
  const entries = value ?? []

  const classifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const { data, isLoading } =
    useClassifierServiceGetIcd10AmClassifierElementListEndpoint(
      {
        id: String(classifierId),
        pageNumber: 1,
        pageSize: DEFAULT_ONE_HUNDRED_PER_PAGE,
        type: Icd10AmElementType.OPPORTUNISTIC_DISEASE
      },
      [classifierId, Icd10AmElementType.OPPORTUNISTIC_DISEASE],
      { enabled: !!classifierId, staleTime: 1000 * 60 * 5 }
    )

  const listed = data?.data ?? []
  const listedIds = new Set(listed.map((item) => item.id))
  const others = entries.filter(
    (entry) => !entry.icd10Id || !listedIds.has(entry.icd10Id)
  )

  const byId = (id?: string) => entries.find((entry) => entry.icd10Id === id)

  const toggle = (id: string, checked: boolean) =>
    onChange?.(
      checked
        ? [...entries, { icd10Id: id }]
        : entries.filter((entry) => entry.icd10Id !== id)
    )

  const setDate = (entry: Entry, date: Dayjs | null) =>
    onChange?.(entries.map((x) => (x === entry ? { ...x, date } : x)))

  const setOther = (entry: Entry, icd10Id?: string) =>
    onChange?.(entries.map((x) => (x === entry ? { ...x, icd10Id } : x)))

  const removeOther = (entry: Entry) =>
    onChange?.(entries.filter((x) => x !== entry))

  if (isLoading) return <Spin />

  if (isReadOnly) {
    const formatDate = (entry: Entry) =>
      entry.date ? entry.date.format(dayMonthYearFormatWithDot) : '—'

    return (
      <Flex vertical gap={4}>
        {entries.map((entry, index) => {
          const item = listed.find((x) => x.id === entry.icd10Id)

          return (
            <Flex key={`${entry.icd10Id ?? 'new'}-${index}`} gap={8}>
              {item ? (
                <Text>{`${item.code} - ${item.translation?.[currentLang.value]}`}</Text>
              ) : (
                // "Alte": the select resolves the name of any ICD-10-AM element.
                <HivIcd10Select
                  value={entry.icd10Id}
                  {...readOnlySelectProps}
                />
              )}
              <Text>{formatDate(entry)}</Text>
            </Flex>
          )
        })}
      </Flex>
    )
  }

  return (
    <Flex vertical gap={8}>
      <Row gutter={[24, 8]}>
        {listed.map((item) => {
          const entry = byId(item.id)

          return (
            <Col key={item.id} xs={24} lg={12}>
              <Flex gap={8} align="center" justify="space-between">
                <Checkbox
                  checked={!!entry}
                  onChange={(e) => toggle(String(item.id), e.target.checked)}
                >
                  {`${item.code} - ${item.translation?.[currentLang.value]}`}
                </Checkbox>

                {entry && (
                  <DatePicker
                    value={entry.date}
                    format={dayMonthYearFormatWithDot}
                    disabledDate={disabledFutureDate}
                    status={entry.date ? undefined : 'error'}
                    placeholder={t('hiv_anamnesis.clinical.disease_date')}
                    onChange={(date) => setDate(entry, date)}
                  />
                )}
              </Flex>
            </Col>
          )
        })}
      </Row>

      <Text>{t('hiv_anamnesis.clinical.other_diseases')}</Text>

      {others.map((entry, index) => (
        <Row key={`${entry.icd10Id ?? 'new'}-${index}`} gutter={[24, 8]}>
          <Col xs={24} lg={14}>
            <HivIcd10Select
              value={entry.icd10Id}
              excludeIds={entries
                .map((x) => x.icd10Id)
                .filter((id): id is string => !!id)}
              status={entry.icd10Id ? undefined : 'error'}
              onChange={(id) => setOther(entry, id)}
            />
          </Col>

          <Col xs={20} lg={8}>
            <DatePicker
              className="full-width"
              value={entry.date}
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
              status={entry.date ? undefined : 'error'}
              placeholder={t('hiv_anamnesis.clinical.disease_date')}
              onChange={(date) => setDate(entry, date)}
            />
          </Col>

          <Col xs={4} lg={2}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeOther(entry)}
            />
          </Col>
        </Row>
      ))}

      <div>
        <Button
          icon={<PlusOutlined />}
          onClick={() => onChange?.([...entries, {}])}
        >
          {t('hiv_anamnesis.clinical.add_other_disease')}
        </Button>
      </div>
    </Flex>
  )
}

/** Every disease row needs its ICD-10 element (56); the date is optional since 2026-10-15. */
export const validateOpportunisticDiseases =
  (message: string) => (_: unknown, value?: Entry[] | null) =>
    (value ?? []).some((entry) => !entry.icd10Id)
      ? Promise.reject(message)
      : Promise.resolve()
